import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { TEST_SERIES_TAB } from './TestSeriesTabSwitcher';
import TestSeriesFilters from './TestSeriesFilters';
import { showFiltersForTab } from '../testTab/constants';

export const ALL_TESTS_CATEGORY_TAB = {
  UPCOMING: 'upcoming',
  ONGOING: 'ongoing',
  PREVIOUS_YEAR: 'previous_year',
  PAST: 'past',
  EDUCATOR: 'educator',
  ALL_SERIES: 'all_series'
};

export const MY_TESTS_CATEGORY_TAB = {
  YOUR_UPCOMING: 'your_upcoming',
  YOUR_ATTEMPT: 'your_attempt'
};

const ALL_TESTS_TABS = [
  { id: ALL_TESTS_CATEGORY_TAB.UPCOMING, label: 'Upcoming Tests' },
  { id: ALL_TESTS_CATEGORY_TAB.ONGOING, label: 'Ongoing Tests' },
  { id: ALL_TESTS_CATEGORY_TAB.PREVIOUS_YEAR, label: 'Previous Year Tests' },
  { id: ALL_TESTS_CATEGORY_TAB.PAST, label: 'Past Tests' },
  { id: ALL_TESTS_CATEGORY_TAB.EDUCATOR, label: 'Educator Tests' },
  { id: ALL_TESTS_CATEGORY_TAB.ALL_SERIES, label: 'All Test Series' }
];

const MY_TESTS_TABS = [
  {
    id: MY_TESTS_CATEGORY_TAB.YOUR_UPCOMING,
    label: 'Your upcoming tests'
  },
  { id: MY_TESTS_CATEGORY_TAB.YOUR_ATTEMPT, label: 'Your Attempt' }
];

export const getDefaultCategoryTab = (view) =>
  view === TEST_SERIES_TAB.MY
    ? MY_TESTS_CATEGORY_TAB.YOUR_UPCOMING
    : ALL_TESTS_CATEGORY_TAB.UPCOMING;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_4B} 0;
  background: var(--color-i-white);
  border-radius: 0 0 12px 12px;
  overflow: visible;

  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_2B};
    padding: ${Spacings.SPACING_4B} 0;
  }
`;

const CategoryTabsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
  width: 100%;
  padding: 0 ${Spacings.SPACING_4B};
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CategoryTab = styled.button`
  position: relative;
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 ${Spacings.SPACING_4B};
  border: none;
  cursor: pointer;
  background: transparent;
  font-size: 14px;
  line-height: normal;
  white-space: nowrap;
  color: ${({ $isActive }) =>
    $isActive ? 'var(--color-accent-blue)' : '#607798'};
  font-weight: ${({ $isActive }) => ($isActive ? Fonts.BOLD : Fonts.REGULAR)};

  &::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 2px;
    border-radius: 4px 4px 0 0;
    background: var(--color-accent-blue);
    opacity: ${({ $isActive }) => ($isActive ? 1 : 0)};
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent-blue);
    outline-offset: 2px;
  }
`;

const FiltersSection = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  min-height: 40px;
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B} 0;
  overflow: visible;
  box-sizing: border-box;
`;

const TestSeriesContentPanel = ({
  view,
  activeCategoryTab,
  onCategoryTabChange,
  filterConfig,
  appliedSelections,
  onAppliedSelectionsChange
}) => {
  const categoryTabs =
    view === TEST_SERIES_TAB.MY ? MY_TESTS_TABS : ALL_TESTS_TABS;
  const shouldShowFilters = showFiltersForTab(view, activeCategoryTab);

  return (
    <Panel>
      <CategoryTabsRow role="tablist" aria-label="Test series categories">
        {categoryTabs.map(({ id, label }) => {
          const isActive = activeCategoryTab === id;

          return (
            <CategoryTab
              key={id}
              type="button"
              role="tab"
              aria-selected={isActive}
              $isActive={isActive}
              onClick={() => onCategoryTabChange(id)}
            >
              {label}
            </CategoryTab>
          );
        })}
      </CategoryTabsRow>
      {shouldShowFilters ? (
        <FiltersSection>
          <TestSeriesFilters
            filterConfig={filterConfig}
            appliedSelections={appliedSelections}
            onAppliedSelectionsChange={onAppliedSelectionsChange}
          />
        </FiltersSection>
      ) : null}
    </Panel>
  );
};

export default TestSeriesContentPanel;

TestSeriesContentPanel.propTypes = {
  view: PropTypes.oneOf([TEST_SERIES_TAB.ALL, TEST_SERIES_TAB.MY]).isRequired,
  activeCategoryTab: PropTypes.string.isRequired,
  onCategoryTabChange: PropTypes.func.isRequired,
  filterConfig: PropTypes.arrayOf(PropTypes.object),
  appliedSelections: PropTypes.object.isRequired,
  onAppliedSelectionsChange: PropTypes.func.isRequired
};

TestSeriesContentPanel.defaultProps = {
  filterConfig: []
};
